/**
 * SkipToResultsLoader Component
 *
 * Minimal waiting experience for paid users who want efficiency.
 * No percentage progress bar (LLM completion time is unknown).
 *
 * Features:
 * - Shows user's submitted message at top (confirmation)
 * - Indeterminate loading indicator (pulse animation)
 * - Rotating stage messages that cycle through analysis phases
 * - Reduced-motion accessibility support (WCAG 2.1 SC 2.3.3)
 * - Mobile-first, Y2K gradient styling
 *
 * NAVIGATION PATTERN:
 * The parent component is responsible for detecting API completion and
 * navigating to results. This component is purely presentational - it shows
 * the waiting UI but does not control when to navigate.
 *
 * @example
 * // Parent component handles navigation when API completes
 * useEffect(() => {
 *   if (data?.data && !isPending) {
 *     router.replace({ pathname: '/results/[analysisId]', params: { analysisId } });
 *   }
 * }, [data, isPending]);
 *
 * // SkipToResultsLoader just displays the waiting UI
 * <SkipToResultsLoader message={userMessage} />
 */

import React, { useState, useEffect, memo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  cancelAnimation,
  Easing,
  FadeIn,
  FadeOut,
} from 'react-native-reanimated';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { useReducedMotionWithLoading } from '../../hooks/useReducedMotionWithLoading';
import {
  IRIDESCENT_GRADIENT,
  GLASSMORPHISM,
  TYPOGRAPHY,
} from '../../lib/design-tokens';

/**
 * Stage messages that rotate during analysis.
 * Each stage represents a phase of the LLM analysis pipeline.
 */
const STAGE_MESSAGES = [
  'skip_to_results.analyzing_message',
  'skip_to_results.detecting_patterns',
  'skip_to_results.generating_insights',
  'skip_to_results.preparing_results',
] as const;

/** Interval between stage message rotations (ms) */
const STAGE_ROTATION_INTERVAL = 2500;

/** Duration for fade transitions between stages (ms) */
const STAGE_FADE_DURATION = 300;

interface SkipToResultsLoaderProps {
  /** The user's submitted message text */
  message: string;
  /** Optional test ID for E2E tests */
  testID?: string;
}

/**
 * Custom hook for rotating stage messages.
 */
function useStageRotation(reducedMotion: boolean) {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);

  useEffect(() => {
    if (reducedMotion) {
      // Skip rotation for reduced motion preference
      return;
    }

    const interval = setInterval(() => {
      setCurrentStageIndex((prev) => (prev + 1) % STAGE_MESSAGES.length);
    }, STAGE_ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [reducedMotion]);

  return STAGE_MESSAGES[currentStageIndex];
}

/**
 * Indeterminate pulse loader component.
 * Uses opacity animation for performance (no blur animation).
 */
const PulseLoader = memo(function PulseLoader({
  reducedMotion,
}: {
  reducedMotion: boolean;
}) {
  const pulseOpacity = useSharedValue(0.4);
  const pulseScale = useSharedValue(1);

  useEffect(() => {
    if (reducedMotion) {
      // Static indicator for reduced motion
      pulseOpacity.value = 0.7;
      pulseScale.value = 1;
      return;
    }

    // Pulse animation: opacity and subtle scale
    pulseOpacity.value = withRepeat(
      withSequence(
        withTiming(0.9, { duration: 800, easing: Easing.inOut(Easing.ease) }),
        withTiming(0.4, { duration: 800, easing: Easing.inOut(Easing.ease) })
      ),
      -1, // Infinite
      true
    );

    pulseScale.value = withRepeat(
      withSequence(
        withTiming(1.05, { duration: 800, easing: Easing.inOut(Easing.ease) }),
        withTiming(1, { duration: 800, easing: Easing.inOut(Easing.ease) })
      ),
      -1,
      true
    );

    return () => {
      cancelAnimation(pulseOpacity);
      cancelAnimation(pulseScale);
    };
  }, [reducedMotion, pulseOpacity, pulseScale]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: pulseOpacity.value,
    transform: [{ scale: pulseScale.value }],
  }));

  return (
    <Animated.View style={[styles.pulseContainer, animatedStyle]}>
      <LinearGradient
        colors={IRIDESCENT_GRADIENT.colors}
        start={IRIDESCENT_GRADIENT.start}
        end={IRIDESCENT_GRADIENT.end}
        style={styles.pulseGradient}
      />
    </Animated.View>
  );
});

/**
 * Rotating stage message with fade transitions.
 */
const StageMessage = memo(function StageMessage({
  stageKey,
  reducedMotion,
}: {
  stageKey: string;
  reducedMotion: boolean;
}) {
  const { t } = useTranslation('loading');

  // Get translated text with fallback
  const stageText = t(stageKey, { defaultValue: 'Analyzing...' });

  if (reducedMotion) {
    // No animation for reduced motion
    return (
      <Text
        style={styles.stageText}
        accessibilityRole="text"
        accessibilityLiveRegion="polite"
      >
        {stageText}
      </Text>
    );
  }

  return (
    <Animated.Text
      key={stageKey}
      entering={FadeIn.duration(STAGE_FADE_DURATION)}
      exiting={FadeOut.duration(STAGE_FADE_DURATION)}
      style={styles.stageText}
      accessibilityRole="text"
      accessibilityLiveRegion="polite"
    >
      {stageText}
    </Animated.Text>
  );
});

/**
 * Message preview component showing user's submitted text.
 * Truncates long messages with ellipsis.
 */
const MessagePreview = memo(function MessagePreview({
  message,
}: {
  message: string;
}) {
  const { t } = useTranslation('loading');

  // Truncate message for display (max ~100 chars)
  const truncatedMessage =
    message.length > 100 ? `${message.substring(0, 97)}...` : message;

  return (
    <View style={styles.messagePreviewContainer}>
      <Text style={styles.messagePreviewLabel}>
        {t('skip_to_results.your_message', { defaultValue: 'Your message' })}
      </Text>
      <View style={styles.messagePreviewCard}>
        <Text
          style={styles.messagePreviewText}
          numberOfLines={3}
          ellipsizeMode="tail"
        >
          {truncatedMessage}
        </Text>
      </View>
    </View>
  );
});

/**
 * Main SkipToResultsLoader component.
 *
 * Clean, minimal, professional loader for paid users.
 * Mobile-first with Y2K gradient styling.
 */
function SkipToResultsLoaderComponent({
  message,
  testID = 'skip-to-results-loader',
}: SkipToResultsLoaderProps) {
  // Use shared hook; treat undefined (loading) as false for animations
  const reducedMotionPreference = useReducedMotionWithLoading();
  const reducedMotion = reducedMotionPreference ?? false;
  const currentStageKey = useStageRotation(reducedMotion);
  const { t } = useTranslation('loading');

  return (
    <View
      style={styles.container}
      testID={testID}
      accessibilityRole="progressbar"
      accessibilityLabel={t('skip_to_results.analyzing_message', {
        defaultValue: 'Analyzing your message',
      })}
      accessibilityLiveRegion="polite"
    >
      {/* Y2K Gradient Background */}
      <LinearGradient
        colors={['#0a0012', '#1a0a2e', '#0d1117']}
        style={styles.background}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />

      {/* Content Container */}
      <View style={styles.contentContainer}>
        {/* Message Preview - User confirmation */}
        <MessagePreview message={message} />

        {/* Indeterminate Loader */}
        <View style={styles.loaderSection}>
          <PulseLoader reducedMotion={reducedMotion} />
        </View>

        {/* Rotating Stage Messages */}
        <View style={styles.stageMessageContainer}>
          <StageMessage stageKey={currentStageKey} reducedMotion={reducedMotion} />
        </View>

        {/* Subtle footer hint */}
        <Text style={styles.footerHint}>
          {t('skip_to_results.premium_experience', {
            defaultValue: 'Premium experience',
          })}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  background: {
    ...StyleSheet.absoluteFill,
  },
  contentContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 400,
    paddingHorizontal: 24,
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Message Preview
  messagePreviewContainer: {
    width: '100%',
    marginBottom: 48,
  },
  messagePreviewLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: TYPOGRAPHY.xs,
    fontFamily: TYPOGRAPHY.fontBody,
    fontWeight: TYPOGRAPHY.medium as any,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 8,
  },
  messagePreviewCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    padding: 16,
  },
  messagePreviewText: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: TYPOGRAPHY.sm,
    fontFamily: TYPOGRAPHY.fontBody,
    fontWeight: TYPOGRAPHY.regular as any,
    lineHeight: 20,
  },

  // Loader Section
  loaderSection: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
  },
  pulseContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    overflow: 'hidden',
    ...Platform.select({
      ios: GLASSMORPHISM.shadow,
      android: { elevation: 8 },
      web: GLASSMORPHISM.shadowCSS as any,
    }),
  },
  pulseGradient: {
    width: '100%',
    height: '100%',
  },

  // Stage Messages
  stageMessageContainer: {
    height: 32, // Fixed height to prevent layout shift
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stageText: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: TYPOGRAPHY.base,
    fontFamily: TYPOGRAPHY.fontBody,
    fontWeight: TYPOGRAPHY.medium as any,
    textAlign: 'center',
  },

  // Footer
  footerHint: {
    position: 'absolute',
    bottom: 32,
    color: 'rgba(255, 255, 255, 0.3)',
    fontSize: TYPOGRAPHY.xs,
    fontFamily: TYPOGRAPHY.fontBody,
    fontWeight: TYPOGRAPHY.regular as any,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
});

export const SkipToResultsLoader = memo(SkipToResultsLoaderComponent);
export default SkipToResultsLoader;
